import { FormEvent } from 'react'
import { Page, PageHeader, Section } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { useBasePath } from '@/lib/paths'
import { firstError } from '@/lib/utils'
import { ArrowRight } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type Package = { revision: number; manifest_digest: string }
type Signer = { name: string; capacity_label: string; represented_name: string | null }

type Props = {
  formation: WizardFormationRef
  package: Package | null
  signer: Signer | null
  order_total_cents: number
  currency: string
}

function formatCents(cents: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format((cents ?? 0) / 100)
}

export default function SubmissionEdit({ formation, package: pkg, signer, order_total_cents, currency }: Props) {
  const basePath = useBasePath()
  const form = useForm({ quoted_total_fee_cents: order_total_cents })
  const baseError = firstError((form.errors as Record<string, string | undefined>).base)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/submission`)
  }

  return (
    <WizardLayout
      title="Submit your application"
      currentStep="submission"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Submit your application' }]}
      guide={
        <WizardGuide title="Submit your application">
          <p>
            Submitting sends your application to Corppy. Payment comes next — we start work on your formation after
            you pay.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="submission-payment-next">
          Your application isn't filed with the state until you pay. Nothing is charged by submitting.
        </Tip>
      }
    >
      <Page>
        <PageHeader
          title={<>Submit your application</>}
          subtitle={<>Review the summary, then submit. Payment is the next step.</>}
        />

        {baseError && (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-muted/40 p-4 text-sm text-destructive">
            {baseError}
          </div>
        )}

        <Section title="Application summary">
          <dl className="grid gap-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Company</dt>
              <dd className="text-right font-medium">{formation.legal_name ?? 'Your company'}</dd>
            </div>
            {pkg && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Document package</dt>
                <dd className="text-right font-medium">
                  Revision {pkg.revision} · {pkg.manifest_digest}
                </dd>
              </div>
            )}
            {signer && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Authorized by</dt>
                <dd className="text-right font-medium">
                  {signer.name}
                  {signer.represented_name && signer.represented_name !== signer.name ? ` — ${signer.represented_name}` : ''}
                  {' · '}
                  {signer.capacity_label}
                </dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Formation total</dt>
              <dd className="text-right font-medium">{formatCents(order_total_cents, currency)}</dd>
            </div>
          </dl>
        </Section>

        <p className="mt-4 text-sm text-muted-foreground">
          Payment is the next step. Corppy starts preparing and submitting your formation after payment is received.
        </p>

        <form onSubmit={handleSubmit} className="mt-6">
          <WizardActions
            currentStep="submission"
            formationId={formation.id}
            disabled={form.processing}
            className="flex flex-wrap items-center gap-2"
          >
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Submitting…' : 'Submit application'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </form>
      </Page>
    </WizardLayout>
  )
}
